// videoSafetyUtils.js
// Utility functions to prevent and recover from video playback issues

/**
 * Initialize global safety flags and monitoring system
 */
export function initVideoSafetySystem() {
    // Initialize global flags for video operations
    window.timeJumpInProgress = false;
    window.isPlaybackInProgress = false;
    window.isVideoChanging = false;
    window.lastPauseTime = 0;
    window.pauseSource = null;
    window.videoOperationLock = false;
    window.lastTimeUpdate = 0;
    window.bufferingVideos = new Set();
    
    // Add an emergency function to release all locks
    window.releaseVideoLocks = function() {
      console.log('🔓 Emergency: Releasing all video operation locks');
      window.timeJumpInProgress = false;
      window.isPlaybackInProgress = false;
      window.isVideoChanging = false;
      window.videoOperationLock = false;
      window.timelineEventClickInProgress = false;
      window.bufferingVideos.clear();
      
      // Also clear any timeouts
      if (window.timelineAutoplayTimer) {
        clearTimeout(window.timelineAutoplayTimer);
        window.timelineAutoplayTimer = null;
      }
      
      console.log('✅ All video operation locks cleared');
      return '✅ All video operation locks cleared';
    };
    
    // Setup automatic monitoring for stuck flags
    const safetyInterval = startSafetyMonitoring();
    
    console.log('🛡️ Video safety system initialized');
    
    // Return cleanup function
    return () => {
      // Clear the safety monitoring interval
      if (safetyInterval) {
        clearInterval(safetyInterval);
      }
      
      // Clean up global flags
      window.timeJumpInProgress = false;
      window.isPlaybackInProgress = false;
      window.isVideoChanging = false;
      window.videoOperationLock = false;
      window.timelineEventClickInProgress = false;
      window.bufferingVideos.clear();
      
      // Clear any remaining timeouts
      if (window.timelineAutoplayTimer) {
        clearTimeout(window.timelineAutoplayTimer);
        window.timelineAutoplayTimer = null;
      }
      
      // Remove the emergency function
      delete window.releaseVideoLocks;
      
      console.log('🛡️ Video safety system cleaned up');
    };
}
  
/**
 * Start the monitoring system to detect stuck operation flags
 */
function startSafetyMonitoring() {
    // Check for stuck flags every 5 seconds
    const safetyInterval = setInterval(() => {
      // Helper function to check timestamp-based flags
      const checkTimeFlag = (flag, flagName, maxAge = 5000) => {
        if (flag && typeof flag === 'number' && (Date.now() - flag > maxAge)) {
          console.warn(`⚠️ Detected stuck ${flagName} flag, forcing release`);
          return false;
        }
        return flag;
      };
      
      // Check all timestamp-based flags
      if (typeof window.timeJumpInProgress === 'number') {
        window.timeJumpInProgress = checkTimeFlag(
          window.timeJumpInProgress, 
          'timeJumpInProgress',
          10000 // 10 seconds for time jumps since they may involve buffering
        );
      }
      
      if (typeof window.isPlaybackInProgress === 'number') {
        window.isPlaybackInProgress = checkTimeFlag(
          window.isPlaybackInProgress, 
          'isPlaybackInProgress'
        );
      }
      
      if (typeof window.isVideoChanging === 'number') {
        window.isVideoChanging = checkTimeFlag(
          window.isVideoChanging, 
          'isVideoChanging',
          10000 // 10 seconds for video changes
        );
      }
      
      // Check boolean flags with associated timestamps
      if (window.timelineEventClickInProgress && 
          window.timelineEventClickStartTime &&
          (Date.now() - window.timelineEventClickStartTime > 5000)) {
        console.warn('⚠️ Detected stuck timelineEventClickInProgress flag, forcing release');
        window.timelineEventClickInProgress = false;
        window.timelineEventClickStartTime = null;
      }
      
      // Check for stuck timeouts
      if (window.timelineAutoplayTimer && 
          window.timelineAutoplayTimerStart && 
          (Date.now() - window.timelineAutoplayTimerStart > 5000)) {
        console.warn('⚠️ Clearing stuck timelineAutoplayTimer');
        clearTimeout(window.timelineAutoplayTimer);
        window.timelineAutoplayTimer = null;
        window.timelineAutoplayTimerStart = null;
      }
      
      // Clear very old buffering videos from the set
      if (window.bufferingVideos && window.bufferingVideos.size > 0) {
        for (const entry of window.bufferingVideos) {
          if (Date.now() - entry.timestamp > 20000) { // 20 seconds max buffer time
            console.warn(`⚠️ Clearing stuck buffering state for video ${entry.id}`);
            window.bufferingVideos.delete(entry);
          }
        }
      }
      
    }, 5000);
    
    // Clean up interval if window is closed
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        clearInterval(safetyInterval);
      });
    }
    
    return safetyInterval;
}
  
/**
 * Mark a video as buffering
 * @param {string} videoId - Identifier for the video
 */
export function markVideoBuffering(videoId) {
    if (!window.bufferingVideos) {
      window.bufferingVideos = new Set();
    }
    
    window.bufferingVideos.add({
      id: videoId,
      timestamp: Date.now()
    });
    
    console.log(`⏳ Video ${videoId} marked as buffering`);
}
  
/**
 * Clear buffering state for a video
 * @param {string} videoId - Identifier for the video
 */
export function clearVideoBuffering(videoId) {
    if (!window.bufferingVideos) return;
    
    // Find the entry with this id
    for (const entry of window.bufferingVideos) {
      if (entry.id === videoId) {
        window.bufferingVideos.delete(entry);
        console.log(`✅ Video ${videoId} buffering state cleared`);
        break;
      }
    }
}
  
/**
 * Use a timeout-protected promise for video operations
 * @param {HTMLVideoElement} videoElement - The video element
 * @param {string} operation - Operation name ('play', 'pause', or 'seek')
 * @param {any} params - Parameters for the operation
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise} - Promise that resolves when operation completes or times out
 */
export function safeVideoOperation(videoElement, operation, params, timeout = 5000) {
    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeoutId = setTimeout(() => {
        console.warn(`⚠️ Video ${operation} operation timed out after ${timeout}ms`);
        resolve(false); // Resolve with false rather than rejecting to prevent error cascades
      }, timeout);
      
      try {
        let operationPromise;
        
        switch (operation) {
          case 'play':
            operationPromise = videoElement.play();
            break;
          case 'pause':
            videoElement.pause();
            operationPromise = Promise.resolve(true);
            break;
          case 'seek':
            videoElement.currentTime = params;
            operationPromise = new Promise(seekResolve => {
              // Listen for seeking and seeked events
              const handleSeeked = () => {
                videoElement.removeEventListener('seeked', handleSeeked);
                videoElement.removeEventListener('error', handleError);
                clearTimeout(timeoutId);
                seekResolve(true);
              };
              
              const handleError = (error) => {
                videoElement.removeEventListener('seeked', handleSeeked);
                videoElement.removeEventListener('error', handleError);
                clearTimeout(timeoutId);
                console.error(`❌ Error during seek operation:`, error);
                seekResolve(false);
              };
              
              videoElement.addEventListener('seeked', handleSeeked);
              videoElement.addEventListener('error', handleError);
            });
            break;
          default:
            clearTimeout(timeoutId);
            reject(new Error(`Unknown video operation: ${operation}`));
            return;
        }
        
        // For play operation which returns a promise
        if (operationPromise && typeof operationPromise.then === 'function') {
          operationPromise
            .then(() => {
              clearTimeout(timeoutId);
              resolve(true);
            })
            .catch(error => {
              clearTimeout(timeoutId);
              console.warn(`⚠️ Video ${operation} operation failed:`, error);
              resolve(false);
            });
        }
        
      } catch (error) {
        clearTimeout(timeoutId);
        console.error(`❌ Exception during video ${operation}:`, error);
        resolve(false);
      }
    });
}
  
/**
 * Create helper for using the safety utilities
 * @param {string} componentName - Name of the component for logging
 * @returns {Object} - Object with safety utility methods
 */
export function createVideoSafetyHelper(componentName) {
    return {
      // Safely play a video with timeout protection
      play: async (videoElement, id = 'unknown') => {
        console.log(`[${componentName}] ▶️ Attempting to play video ${id}`);
        return safeVideoOperation(videoElement, 'play', null);
      },
      
      // Safely pause a video
      pause: (videoElement, id = 'unknown') => {
        console.log(`[${componentName}] ⏸️ Pausing video ${id}`);
        return safeVideoOperation(videoElement, 'pause', null);
      },
      
      // Safely seek to a time
      seekTo: async (videoElement, time, id = 'unknown') => {
        console.log(`[${componentName}] ⏩ Seeking video ${id} to ${time}s`);
        return safeVideoOperation(videoElement, 'seek', time);
      },
      
      // Mark as buffering
      startBuffering: (id) => markVideoBuffering(id),
      
      // Clear buffering state
      endBuffering: (id) => clearVideoBuffering(id),
      
      // Emergency reset all locks
      resetAll: () => window.releaseVideoLocks()
    };
}
  
export default {
    initVideoSafetySystem,
    markVideoBuffering,
    clearVideoBuffering,
    safeVideoOperation,
    createVideoSafetyHelper
};